import { useState, useEffect } from 'react';
import { X, Plus, Trash2, Save } from 'lucide-react';
import type { InboxHooks, InboxPlatformConfig, InboxStore, GenericAgentConfig } from '../types';
import styles from './GenericAgentConfigPanel.module.css';

interface Props {
    hooks: InboxHooks;
    store: InboxStore;
    platform: InboxPlatformConfig;
}

const BASE_EMPTY_CONFIG: GenericAgentConfig = {
    name: '',
    systemPrompt: '',
    isActive: true,
    isDefault: false,
    maxResponseLength: 500,
    responseDelayMs: 2000,
    handoffKeywords: '["hablar con una persona", "agente humano", "talk to a person"]',
    autoGreet: true,
    greetingMessage: '',
    includeBusinessContext: true,
    maxHistoryMessages: 20,
};

function buildEmptyConfig(platform: InboxPlatformConfig): GenericAgentConfig {
    const base = { ...BASE_EMPTY_CONFIG };
    if (platform.name === 'instagram') {
        return {
            ...base,
            iceBreakers: '[]',
            storyMentionAutoReply: false,
            storyMentionReplyTemplate: '',
        };
    }
    if (platform.name === 'messenger') {
        return {
            ...base,
            iceBreakers: '[]',
            persistentMenu: '[]',
        };
    }
    return base;
}

export function GenericAgentConfigPanel({ hooks, store, platform }: Props) {
    const { showAgentConfig, setShowAgentConfig } = store;
    const { data: configs } = hooks.useAgentConfigs();
    const saveConfig = hooks.useSaveAgentConfig();
    const deleteConfig = hooks.useDeleteAgentConfig();

    const emptyConfig = buildEmptyConfig(platform);
    const [selected, setSelected] = useState<GenericAgentConfig>(emptyConfig);
    const [isNew, setIsNew] = useState(true);

    useEffect(() => {
        if (configs && configs.length > 0 && isNew && !selected.id) {
            setSelected(configs[0] as GenericAgentConfig);
            setIsNew(false);
        }
    }, [configs]);

    if (!showAgentConfig) return null;

    const handleSelect = (config: GenericAgentConfig) => {
        setSelected(config);
        setIsNew(false);
    };

    const handleNew = () => {
        setSelected({ ...emptyConfig });
        setIsNew(true);
    };

    const handleSave = () => {
        if (!selected.name || !selected.systemPrompt) return;
        saveConfig.mutate(selected as GenericAgentConfig & { name: string; systemPrompt: string }, {
            onSuccess: (saved) => {
                setSelected(saved as GenericAgentConfig);
                setIsNew(false);
            },
        });
    };

    const handleDelete = () => {
        if (!selected.id) return;
        deleteConfig.mutate(selected.id, {
            onSuccess: () => {
                setSelected({ ...emptyConfig });
                setIsNew(true);
            },
        });
    };

    const PlatformIcon = platform.icon;
    const panelTitle = `${platform.label} Agent Configuration`;

    return (
        <div className={styles.overlay} onClick={() => setShowAgentConfig(false)}>
            <div className={styles.panel} onClick={(e) => e.stopPropagation()}>
                <div className={styles.header}>
                    <div className={styles.headerLeft}>
                        <PlatformIcon className={styles.platformIcon} />
                        <h3 className={styles.title}>{panelTitle}</h3>
                    </div>
                    <button onClick={() => setShowAgentConfig(false)} className={styles.closeBtn}>
                        <X className={styles.closeIcon} />
                    </button>
                </div>

                <div className={styles.body}>
                    {/* Config selector */}
                    <div className={styles.configSelector}>
                        <div className={styles.configList}>
                            {(configs ?? []).map((c) => (
                                <button
                                    key={c.id}
                                    onClick={() => handleSelect(c as GenericAgentConfig)}
                                    className={`${styles.configItem} ${selected.id === c.id ? styles.configItemActive : ''}`}
                                >
                                    {c.name}
                                    {c.isDefault && <span className={styles.defaultBadge}>Default</span>}
                                </button>
                            ))}
                        </div>
                        <button onClick={handleNew} className={styles.newBtn}>
                            <Plus className={styles.newIcon} /> New
                        </button>
                    </div>

                    {/* Form — fields common to all platforms */}
                    <div className={styles.form}>
                        <label className={styles.label}>
                            Name
                            <input
                                type="text"
                                value={selected.name ?? ''}
                                onChange={(e) => setSelected({ ...selected, name: e.target.value })}
                                className={styles.input}
                            />
                        </label>

                        <label className={styles.label}>
                            System Prompt
                            <textarea
                                value={selected.systemPrompt ?? ''}
                                onChange={(e) => setSelected({ ...selected, systemPrompt: e.target.value })}
                                className={styles.textarea}
                                rows={6}
                            />
                        </label>

                        <label className={styles.label}>
                            Handoff Keywords (JSON array)
                            <textarea
                                value={selected.handoffKeywords ?? '[]'}
                                onChange={(e) => setSelected({ ...selected, handoffKeywords: e.target.value })}
                                className={styles.textarea}
                                rows={2}
                            />
                        </label>

                        <label className={styles.label}>
                            Greeting Message
                            <input
                                type="text"
                                value={selected.greetingMessage ?? ''}
                                onChange={(e) => setSelected({ ...selected, greetingMessage: e.target.value })}
                                className={styles.input}
                            />
                        </label>

                        {/* Instagram and Messenger: Ice Breakers */}
                        {(platform.name === 'instagram' || platform.name === 'messenger') && (
                            <label className={styles.label}>
                                Ice Breakers (JSON array)
                                {platform.name === 'messenger' && (
                                    <span className={styles.fieldHint}>
                                        Quick reply suggestions shown to new users. Example:{' '}
                                        {`[{"question": "What are your hours?", "payload": "HOURS"}]`}
                                    </span>
                                )}
                                <textarea
                                    value={selected.iceBreakers ?? '[]'}
                                    onChange={(e) => setSelected({ ...selected, iceBreakers: e.target.value })}
                                    className={styles.textarea}
                                    rows={platform.name === 'messenger' ? 4 : 3}
                                    placeholder={
                                        platform.name === 'messenger'
                                            ? '[{"question": "What are your hours?", "payload": "HOURS"}]'
                                            : '["Hi! How can I help you?", "Ask me anything"]'
                                    }
                                />
                            </label>
                        )}

                        <div className={styles.row}>
                            <label className={styles.label}>
                                Max Response Length
                                <input
                                    type="number"
                                    value={selected.maxResponseLength ?? 500}
                                    onChange={(e) => setSelected({ ...selected, maxResponseLength: Number(e.target.value) })}
                                    className={styles.input}
                                />
                            </label>
                            <label className={styles.label}>
                                Response Delay (ms)
                                <input
                                    type="number"
                                    value={selected.responseDelayMs ?? 2000}
                                    onChange={(e) => setSelected({ ...selected, responseDelayMs: Number(e.target.value) })}
                                    className={styles.input}
                                />
                            </label>
                            <label className={styles.label}>
                                History Messages
                                <input
                                    type="number"
                                    value={selected.maxHistoryMessages ?? 20}
                                    onChange={(e) => setSelected({ ...selected, maxHistoryMessages: Number(e.target.value) })}
                                    className={styles.input}
                                />
                            </label>
                        </div>

                        <div className={styles.checkboxRow}>
                            <label className={styles.checkbox}>
                                <input
                                    type="checkbox"
                                    checked={selected.isActive ?? true}
                                    onChange={(e) => setSelected({ ...selected, isActive: e.target.checked })}
                                />
                                Active
                            </label>
                            <label className={styles.checkbox}>
                                <input
                                    type="checkbox"
                                    checked={selected.isDefault ?? false}
                                    onChange={(e) => setSelected({ ...selected, isDefault: e.target.checked })}
                                />
                                Default
                            </label>
                            <label className={styles.checkbox}>
                                <input
                                    type="checkbox"
                                    checked={selected.autoGreet ?? true}
                                    onChange={(e) => setSelected({ ...selected, autoGreet: e.target.checked })}
                                />
                                Auto Greet
                            </label>
                            <label className={styles.checkbox}>
                                <input
                                    type="checkbox"
                                    checked={selected.includeBusinessContext ?? true}
                                    onChange={(e) => setSelected({ ...selected, includeBusinessContext: e.target.checked })}
                                />
                                Include Business Context
                            </label>
                        </div>

                        {/* ── Instagram-specific: Story Mention ──────────────── */}
                        {platform.name === 'instagram' && (
                            <div className={styles.platformSection}>
                                <h4 className={styles.platformSectionTitle}>Story Mention Settings</h4>
                                <div className={styles.checkboxRow}>
                                    <label className={styles.checkbox}>
                                        <input
                                            type="checkbox"
                                            checked={selected.storyMentionAutoReply ?? false}
                                            onChange={(e) =>
                                                setSelected({ ...selected, storyMentionAutoReply: e.target.checked })
                                            }
                                        />
                                        Auto-reply to story mentions
                                    </label>
                                </div>
                                {selected.storyMentionAutoReply && (
                                    <label className={styles.label}>
                                        Story Mention Reply Template
                                        <textarea
                                            value={selected.storyMentionReplyTemplate ?? ''}
                                            onChange={(e) =>
                                                setSelected({ ...selected, storyMentionReplyTemplate: e.target.value })
                                            }
                                            className={styles.textarea}
                                            rows={3}
                                            placeholder="Thanks for mentioning us in your story! ..."
                                        />
                                    </label>
                                )}
                            </div>
                        )}

                        {/* ── Messenger-specific: Persistent Menu ────────────── */}
                        {platform.name === 'messenger' && (
                            <>
                                <div className={styles.sectionDivider}>
                                    <span className={styles.sectionLabel}>Messenger-specific Settings</span>
                                </div>
                                <label className={styles.label}>
                                    Persistent Menu (JSON array)
                                    <span className={styles.fieldHint}>
                                        Menu items shown in the chat header. Example:{' '}
                                        {`[{"title": "Talk to a human", "payload": "HANDOFF"}]`}
                                    </span>
                                    <textarea
                                        value={selected.persistentMenu ?? '[]'}
                                        onChange={(e) => setSelected({ ...selected, persistentMenu: e.target.value })}
                                        className={styles.textarea}
                                        rows={4}
                                        placeholder='[{"title": "Talk to a human", "payload": "HANDOFF"}]'
                                    />
                                </label>
                            </>
                        )}
                    </div>
                </div>

                <div className={styles.footer}>
                    {!isNew && selected.id && (
                        <button
                            onClick={handleDelete}
                            className={styles.deleteBtn}
                            disabled={deleteConfig.isPending}
                        >
                            <Trash2 className={styles.footerIcon} /> Delete
                        </button>
                    )}
                    <button
                        onClick={handleSave}
                        className={styles.saveBtn}
                        disabled={saveConfig.isPending}
                    >
                        <Save className={styles.footerIcon} /> {isNew ? 'Create' : 'Save'}
                    </button>
                </div>
            </div>
        </div>
    );
}
